import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Container, Row, Col } from "react-bootstrap/esm/index";
import Helmet from "react-helmet";
import { helmetJsonLdProp } from "react-schemaorg";

// Import Components
import ITPHeroHeader from "../../components/HeroHeader/ITPHeroHeader";
import ItpNavigation from "../../components/ITPNavigation/ITPNavigation";
import FooterITP from "../../components/shared-ui/Footer/Footer-ITP";
import BackToTopBtn from "../../components/shared-ui/BackToTopBtn/BackToTopBtn";
import ISIITPinline from "../../components/shared-ui/ISI/ISIITP-inline";
import ISIwrapper from "../../components/shared-ui/ISI/ISI-wrapper";
import ISIITP from "../../components/shared-ui/ISI/ISIITP";
import LookingForSupport from "../../components/shared-ui/Cards/LookingForSupport";

// Import Styles
import "../../css/WhatIsITP.scss";
import PrimaryButton from "../../components/shared-ui/Buttons/PrimaryButton";

function WhatIsITP() {
  useEffect(() => {
    window.scrollTo(0, 0);
    let location = window.location.pathname;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      page: {
        path: `${location}`,
        title:
          "What is Immune Thrombocytopenia (ITP) | DOPTELET® (avatrombopag)",
        environment: "dev",
        indication: "ITP",
        audience: "DTC",
      },
    });
  }, []);

  const showPhreesiaModal = (event) => {
    let modal = document.querySelector("#phreesia-modal");
    document.body.classList.add("no-scroll");
    modal.classList.remove("fadeOut");
    modal.style.display = "block";
    modal.classList.add("fadeIn");
    modal.style.height = "100%";
    modal.querySelector('.exitBtn').addEventListener(
      "click",
      function() {
        modal.style.display = "none";
        document.body.classList.remove("no-scroll");
      }
    );
    modal.querySelector('.modal-overlay').addEventListener(
      "click",
      function() {
        modal.style.display = "none";
        document.body.classList.remove("no-scroll");
      }
    );
  };

  return (
    <div className="main_content_wrap">
      <Helmet
        script={[
          helmetJsonLdProp({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "MedicalWebPage",
                name: "What is ITP?",
                description:
                  "Learn about immune thrombocytopenia (ITP), a condition characterized by low platelet count and its symptoms, causes, diagnosis, and treatment options.",
                url: "https://doptelet.com/what-is-itp/",
                mainEntity: {
                  "@type": "MedicalCondition",
                  name: "Immune Thrombocytopenia (ITP)",
                  alternateName: "Idiopathic Thrombocytopenic Purpura",
                  description:
                    "Immune thrombocytopenia (ITP) is a condition characterized by low platelet count due to the immune system mistakenly attacking and destroying platelets.",
                  associatedAnatomy: {
                    "@type": "AnatomicalStructure",
                    name: "Blood",
                    description:
                      "The blood plays a crucial role in carrying oxygen, nutrients, and platelets throughout the body.",
                  },
                  possibleTreatment: {
                    "@type": "MedicalTherapy",
                    name: "Treatment options for ITP",
                    description:
                      "Treatment options for ITP include corticosteroids, immune globulins, splenectomy, and thrombopoietin receptor agonists like Doptelet.",
                  },
                },
              },
              {
                "@type": "Drug",
                url: "https://doptelet.com/what-is-doptelet/",
                name: "Doptelet",
                description:
                  "Doptelet (avatrombopag) is a medication approved for the treatment of chronic immune thrombocytopenia (ITP) in certain adult patients.",
                drugClass: "Thrombopoietin Receptor Agonist",
                mechanismOfAction:
                  "Doptelet interacts with the thrombopoietin receptor to stimulate the production and differentiation of megakaryocytes, leading to increased platelet production.",
              },
              {
                "@type": "WebSite",
                about: {
                  "@type": "Thing",
                  description:
                    "To inform patients of Doptelet (avatrombopag), a prescription medicine used to treat low blood platelet counts in adults with chronic immune thrombocytopenia when other treatments have not worked well enough. Doptelet (avatrombopag) is also a prescription medicine used to treat low blood platelet counts in adults with long-lasting (chronic) liver disease who are scheduled to have a medical or dental procedure.",
                },
                url: "https://doptelethcp.com/what-is-itp/",
              },
              {
                "@type": "Brand",
                logo: "https://doptelet.com/images/doptelet-avatrombopag-tablets-logo.svg",
              },
              {
                "@type": "Brand",
                logo: "https://doptelet.com/images/sobi-logo.png",
              },
              {
                "@type": "MedicalSignOrSymptom",
                url: "https://doptelet.com/what-is-itp/",
                name: "Easy Bruising",
                description:
                  "Easy bruising is a common symptom of immune thrombocytopenia (ITP), where even minor injuries can result in large bruises due to low platelet count.",
              },
              {
                "@type": "MedicalIndication",
                url: "https://doptelet.com/what-is-itp/",
                name: "Chronic Immune Thrombocytopenia (ITP)",
                description:
                  "Doptelet is indicated for the treatment of thrombocytopenia in adult patients with chronic immune thrombocytopenia (ITP) who have had an insufficient response to previous treatment.",
              },
              {
                "@type": "MedicalTherapy",
                url: "https://doptelet.com/what-is-itp/",
                name: "Thrombopoietin (TPO) Receptor Agonists in ITP Treatment",
                description:
                  "Thrombopoietin receptor agonists (TPO-RAs), like Doptelet, are used as a medical therapy for the treatment of immune thrombocytopenia (ITP) in certain adult patients. It helps to stimulate platelet production and raise platelet counts in the blood.",
                drug: {
                  "@type": "Drug",
                  name: "Doptelet",
                  alternateName: "Avatrombopag",
                },
              },
              {
                "@type": "MedicalCause",
                url: "https://doptelet.com/what-is-itp/",
                name: "Immune Thrombocytopenia",
                description:
                  "Immune thrombocytopenia (ITP) is a condition characterized by low platelet count due to the immune system mistakenly attacking and destroying platelets.",
              },
              {
                "@type": "MedicalGuideline",
                url: "https://doptelet.com/what-is-itp/",
                name: "Chronic Immune Thrombocytopenia (ITP) Treatment Guidelines",
                description:
                  "Explore the treatment guidelines for chronic immune thrombocytopenia (ITP) to better understand the condition and available treatment options.",
                guidelineSubject: {
                  "@type": "MedicalEntity",
                  name: "Chronic immune thrombocytopenia (ITP)",
                  alternateName: "Idiopathic Thrombocytopenic Purpura",
                  description:
                    "Immune thrombocytopenia (ITP), also known as idiopathic thrombocytopenic purpura, is a blood disorder characterized by low platelet count due to the immune system mistakenly attacking and destroying platelets.",
                },
                code: {
                  "@type": "MedicalCode",
                  codeValue: "D69.3",
                  codingSystem: "ICD-10",
                },
                evidenceLevel: "Level B",
                evidenceOrigin: "Clinical trials and expert consensus",
                guidelineDate: "2022-01-01",
              },
            ],
          }),
        ]}>
        <title>
          What is Immune Thrombocytopenia (ITP)? | DOPTELET® (avatrombopag)
        </title>
        <meta
          name="description"
          content="Learn about chronic ITP, a condition that causes a shortage of platelets in your blood. Get information that can help you understand how it may affect you."
        />
        <link rel="canonical" href="https://doptelet.com/what-is-itp/" />
      </Helmet>
      <ItpNavigation />
      <div className="inner-content-wrap">
        <ITPHeroHeader
          extraClass="large-hero inner-bg"
          markup={
            <>
              <h1>WHAT IS CHRONIC ITP?</h1>
              <p className="mb-0">
                Immune thrombocytopenia (ITP) is a rare disease, so it’s natural to have lots of questions. Here you’ll find information about what causes ITP, how it’s diagnosed, and how it may be treated.
              </p>
            </>
          }
          maxWidth="513"
          bgImage={["PlateyCarnivalBalloons.png", "PlateyCarnivalBalloons.png"]}
          bgImageMobile={[
            "what-is-itp-hero-mobile.png",
            "what-is-itp-hero-mobile@2x.png",
          ]}
          imageAlt="Doptelet platelet characters holding colorful balloons in carnival scenery"
        />

        <section className="section-padding">
          <Container>
            <Row className="align-items-center">
              <Col lg={{ span: 12 }}>
                <h2>ITP is a shortage of platelets in your blood</h2>
                <p className="mb-0">
                  Your immune system’s job is to protect against infection by making antibodies that attack, destroy, and remove foreign substances in the body. ITP is a blood disorder that causes your immune system to produce fewer platelets overall and mistake your platelets for foreign substances and destroy them. This is a serious problem because the main role of platelets is to prevent and stop bleeding in your body.
                </p>
              </Col>
            </Row>
          </Container>
        </section>

        {/*<div className="dopteller-diaries video-section">
          <Container>
            <Row>
              <Col lg={5} className="align-content-center">
                <h2>
                  Learn more about chronic ITP
                </h2>
                <p className="pr-0 mb-0">
                  In this video, you will learn about your immune system and what happens in your body with chronic ITP.
                </p>
                <div className="video-wrapper d-lg-none">
                  <YoutubeEmbed
                    embedId="BuIhQABk2-A"
                    image={["tammys_tips_getting_the_most.png"]}
                    imageMobile={["tammys_tips_getting_the_most.png"]}
                    alt="Play Video: Tammy's Tips: Getting the Most Out of Your Doctor's Appointments"
                  />
                </div>
              </Col>
              <Col lg={7} className="d-none d-lg-block">
                <div className="video-wrapper">
                  <YoutubeEmbed
                    embedId="BuIhQABk2-A"
                    image={[
                      "tammys_tips_getting_the_most.png",
                      "tammys_tips_getting_the_most@pngjpg",
                    ]}
                    imageMobile={[
                      "tammys_tips_getting_the_most.png",
                      "tammys_tips_getting_the_most@pngjpg",
                    ]}
                    alt="Play Video: Tammy's Tips: Getting the Most Out of Your Doctor's Appointments"
                  />
                </div>
              </Col>
            </Row>
          </Container>
        </div>*/}

        <section className="cloud-blue-bg section-padding how-is-itp-diagnosed scroll-to-section" id="diagnosing">
          <Container>
            <Row>
              <Col md={6} className="align-content-center">
                <h2 className="mb-3">How is ITP diagnosed?</h2>
                <picture>
                  <source
                    srcSet="/images/soft-serve.png"
                    type="image/png"
                  />
                  <img
                    src="/images/soft-serve.png"
                    alt="Doptelet platelet characters getting ice cream cones at a soft serve stand"
                    className="d-lg-none d-block m-auto"
                  />
                </picture>
                <p>
                  There’s no specific way to test for ITP—it’s sometimes referred to as a “diagnosis of exclusion.” That means your doctor will try to rule out other possible causes of low platelets, like an underlying illness you may have or any medication you may be taking. If no other cause is found, it may result in a diagnosis of ITP. And if ITP persists for over a year, you may be diagnosed with “chronic ITP.”
                </p>
              </Col>
              <Col md={6} className="d-none d-lg-block">
                <picture>
                  <source
                    srcSet="/images/soft-serve.png"
                    type="image/png"
                  />
                  <img
                    src="/images/soft-serve.png"
                    alt="Doptelet platelet characters getting ice cream cones at a soft serve stand"
                    className="m-auto d-none d-md-block"
                  />
                </picture>
              </Col>
            </Row>
          </Container>
        </section>

        <section className="section-padding">
          <Container>
            <Row className="align-items-center">
              <Col lg={{ span: 12 }}>
                <h2>ITP diagnostic tests</h2>
                <p className="mb-0">
                  ITP is a rare autoimmune condition that typically doesn’t have a genetic link—but you should let your doctor know if someone in your family has ITP. For your doctor to determine if you have ITP, they may run a few of these common tests:
                </p>
              </Col>
            </Row>
            <Row className="pt-4">
              <Col md={12} lg={4} className="mb-4 mb-lg-0">
                <ul><li><strong className="text-blue">Complete blood count (CBC)</strong><br/>Your doctor will draw a sample of blood and use it to measure your number of blood cells, including your platelet counts</li></ul>
              </Col>
              <Col md={12} lg={4} className="mb-4 mb-lg-0">
                <ul><li><strong className="text-blue">CBC with differential</strong><br/>A thin layer of your blood—normally taken as part of a CBC—is put on a glass slide and reviewed under a microscope to learn more about the number and shape of your blood cells</li></ul>
              </Col>
              <Col md={12} lg={4}>
                <ul><li><strong className="text-blue">Bone marrow examination</strong><br/>In rare cases, if the other blood tests show that your platelet count is low, your doctor may want to collect a sample of your bone marrow (they’ll numb the area beforehand) to make sure it’s making enough platelets</li></ul>
              </Col>
            </Row>
            <Row>
              <Col>
                <p className="mt-4 mb-0"><Link to="/what-is-itp/itp-signs-and-symptoms/">Learn more about ITP</Link> and get answers to the most commonly asked questions.</p>
              </Col>
            </Row>
          </Container>
        </section>

        <section className="section-padding pt-0">
          <Container>
            <Row>
              <Col md={{ span: 12 }}>
                <div className="contained orange-bg d-flex align-items-center flex-column">
                    <h2>Find a chronic ITP specialist near you</h2>
                    <p className="mb-0">
                      One of the most important steps to getting the care you deserve is finding the right hematologist in your area.
                    </p>
                    <PrimaryButton
                      title="FIND A SPECIALIST"
                      addClass="btn btn-primary mb-4 mt-4"
                      onClick={showPhreesiaModal}
                    />
                    <p className="mb-0">Sobi is not responsible for the choices or actions that you or others take because of information provided by this free tool.</p>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        <LookingForSupport addClass="section-padding pt-0" />

        <ISIITPinline />
      </div>
      <FooterITP />
      <ISIwrapper>
        <ISIITP />
      </ISIwrapper>
      <BackToTopBtn />
    </div>
  );
}

export default WhatIsITP;
